import { useRouter } from 'next/router'
import Link from 'next/link'
import { useEffect, useState, type FC } from 'react'
import { isValid } from 'date-fns'

import { Text } from '@wartek-id/text-v1'
import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'

import { Header } from 'app/KinerjaShared/components/Header'
import { getUserDisplayName } from 'utils/user'
import CircularProgressV2 from 'app/KinerjaPSPage/components/CircularProgressV2'
import { PENGELOLAAN_KINERJA_PS_PATH } from 'app/KinerjaShared/constants/path'
import useRoleContext from 'app/KinerjaShared/hooks/useRoleContext'
import DialogPanduanPenetapanPredikatKinerja from 'app/KinerjaPSPage/components/DialogPanduanPenetapanPredikatKinerja'
import { useLocalStorage } from 'utils/hooks/useLocalStorage'
import { PANDUAN_GENERAL_URL } from 'app/KinerjaShared/utils/panduan'
import { useKinerjaPsTrackEvent } from 'app/KinerjaPSPage/hooks/useKinerjaPsTrackEvent'
import usePenetapanCountPs from 'app/KinerjaPSPage/hooks/usePenetapanCountPs'
import usePenilaianCountPs from 'app/KinerjaPSPage/hooks/usePenilaianCountPs'
import usePenetapanGuard from 'app/KinerjaPSPage/hooks/usePenetapanGuard'
import useToggleSdkContext from 'utils/useToogleSdk'
import { formatDate } from 'utils/datetime'
import { useIsPrior2025 } from 'app/KinerjaShared/hooks/useIsPrior2025'
import usePenetapanTrackEvent from 'app/KinerjaShared/hooks/usePenetapanTrackEvent'

interface PenetapanRecommendationBlockProps {
  isPenetapanEnabled: boolean
  isPenetapanStarted: boolean
  penetapanStartDate: Date | null
  isLoading: boolean
  counter: {
    sudahDikirim: number
    all: number
    belumDikirim: number
  }

  onCtaClick: () => void

  title: string
  subtitle: string
}

const PenetapanRecommendationBlock: FC<PenetapanRecommendationBlockProps> = ({
  isPenetapanEnabled,
  isPenetapanStarted,
  isLoading,

  penetapanStartDate,

  counter,

  onCtaClick,

  title,

  subtitle,
}) => {
  const isDoPenetapan = isPenetapanEnabled && isPenetapanStarted

  return (
    <div className="border border-subdued rounded py-6 pl-6 pr-8 justify-between flex flex-col gap-6 flex-1">
      <div className="space-x-6 flex items-center">
        <div className="w-20 h-20 flex-shrink-0 flex items-center justify-center">
          <CircularProgressV2
            isLoading={isLoading}
            value={isDoPenetapan ? counter.sudahDikirim : undefined}
            maxValue={isDoPenetapan ? counter.all : undefined}
            isLocked={!isDoPenetapan}
          />
        </div>
        <div className="space-y-2 w-full flex-1">
          <Text variant="heading-md">{title}</Text>

          <Text color="subdued">{subtitle}</Text>
        </div>
      </div>
      <div className="flex justify-end">
        {isDoPenetapan ? (
          <Button className="flex-shrink-0" color="black" onClick={onCtaClick}>
            Lakukan
          </Button>
        ) : (
          <Button as="a" className="flex-shrink-0" disabled>
            {!isPenetapanStarted && isValid(penetapanStartDate)
              ? `Tersedia mulai ${formatDate(penetapanStartDate)}`
              : 'Lakukan'}
          </Button>
        )}
      </div>
    </div>
  )
}

interface PenilaianKinerjaProps {}

const PenilaianKinerja: FC<PenilaianKinerjaProps> = ({}) => {
  const router = useRouter()

  const toggles = useToggleSdkContext()
  const isPenilaianKsEnabled =
    toggles['kinerja-ps-penilaian-ks-enabled'] || false

  const [currentStatus, setStatus, ,] = useLocalStorage(
    'KINERJA_PS_DIALOG_PANDUAN_PENETAPAN_PREDIKAT_KINERJA',
    null
  )

  const isPriorTo2025 = useIsPrior2025()

  const [isOpen, setIsOpen] = useState(false)

  const { trackOpenHub, trackClickLakukanPenilaianOnHub } =
    useKinerjaPsTrackEvent()
  const { trackEvent } = usePenetapanTrackEvent()

  const {
    isLoading: isLoadingPengawasProfile,
    profilePs: pengawasProfile,
    isPengawasSekolah,
    isKepalaDinasAndPengawasSekolah,
    leave,
  } = useRoleContext()
  if (!isLoadingPengawasProfile && !isPengawasSekolah) leave()

  // Penilaian
  const {
    isLoading: isLoadingPenilaianCount,
    isSuccess: isSuccessPenilaianCount,
    data: counterPenilaian,
  } = usePenilaianCountPs({
    enabled: !!pengawasProfile,
  })

  // Penetapan
  const {
    isLoading: isLoadingPenetapanCount,
    isSuccess: isSuccessPenetapanCount,
    data: counterPenetapan,
    detail: penetapanDetail,
  } = usePenetapanCountPs({
    enabled: !!pengawasProfile,
  })

  const isLoading =
    isLoadingPengawasProfile ||
    isLoadingPenilaianCount ||
    isLoadingPenetapanCount
  const isSuccess =
    !isLoading && isSuccessPenilaianCount && isSuccessPenetapanCount

  const {
    isEnabled: isPenetapanEnabled,
    isStarted: isPenetapanStarted,
    startDate: penetapanStartDate,
  } = usePenetapanGuard()

  const goto = () => {
    router.push(`${PENGELOLAAN_KINERJA_PS_PATH}/penetapan`)
  }

  useEffect(() => {
    if (!!isSuccess) {
      trackOpenHub()
      trackEvent({
        activityType: 'view_hub',
        role: 'tk',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess])

  return (
    <div>
      <Header
        title="Pemantauan Kinerja dan Rekomendasi Predikat Kinerja"
        className="mb-9 print:hidden"
        onBack={() => {
          router.back()
        }}
        rightNode={
          <section className="bg-white rounded py-1">
            <Link href={PANDUAN_GENERAL_URL} passHref legacyBehavior>
              <Button
                as="a"
                variant="ghost"
                target="_blank"
                rel="noreferrer"
                className="flex gap-1.5"
                size="md"
              >
                <Icon fontSize="sm" color="informational">
                  help
                </Icon>
                Ke Pusat Informasi
              </Button>
            </Link>
          </section>
        }
      />

      <section className="mb-9">
        <header className="text-white space-y-1 p-4 bg-gradient-to-r from-pink-70 to-pink-20 rounded-t-lg">
          <Text
            className="overflow-wrap-anywhere line-clamp-1"
            color="inherit"
            variant="heading-lg"
          >
            {isLoading
              ? 'Selamat datang!'
              : `Selamat datang, ${getUserDisplayName(pengawasProfile?.nama)}!`}
          </Text>
          <Text color="inherit">
            Pemantauan Kinerja yang Anda lakukan merupakan aspek penting dalam
            peningkatan kinerja wilayah binaan Anda.
          </Text>
        </header>

        <div className="bg-white rounded-b-lg px-8 py-6">
          <div className="space-y-6">
            {/* Block "Lakukan penilaian" */}
            <div className="border border-subdued rounded py-6 pl-6 pr-8 space-x-6 flex items-center justify-between">
              <div className="space-x-6 flex items-center">
                <div className="w-20 h-20 flex-shrink-0 flex items-center justify-center">
                  <CircularProgressV2
                    isLoading={isLoading}
                    value={counterPenilaian.finish}
                    maxValue={counterPenilaian.all}
                    isLocked={false}
                  />
                </div>
                <div className="space-y-2 w-full">
                  <Text variant="heading-md">Pemantauan Kinerja</Text>
                  <Text color="subdued">
                    Cek Praktik Kinerja, Perilaku Kerja, dan lainnya.
                  </Text>
                </div>
              </div>
              <div className="max-w-60 flex-1">
                <Link
                  href={`${PENGELOLAAN_KINERJA_PS_PATH}/penilaian`}
                  passHref
                >
                  <Button
                    as="a"
                    fullWidth
                    className="flex-shrink-0"
                    color="white"
                    disabled={isLoading || !isPenilaianKsEnabled}
                    onClick={() => {
                      if (!isPenilaianKsEnabled) return

                      trackClickLakukanPenilaianOnHub()
                    }}
                  >
                    {isPenilaianKsEnabled ? 'Cek' : 'Sedang diperbarui'}
                  </Button>
                </Link>
              </div>
            </div>

            {/* Icon */}
            <div className="flex items-center justify-center">
              <Icon className="!cursor-default">arrow_downward</Icon>
            </div>

            {/* Block "Lakukan Penetapan Rekomendasi Predikat Kinerja" */}
            {isPriorTo2025 ? (
              <>
                {(!isPenetapanEnabled || !isPenetapanStarted) && (
                  <div className="border border-subdued rounded py-6 pl-6 pr-8 space-x-6 flex items-center">
                    <div className="w-20 h-20 flex-shrink-0 flex items-center justify-center">
                      <CircularProgressV2
                        isLoading={isLoading}
                        isLocked={true}
                      />
                    </div>
                    <div className="space-y-2 w-full">
                      <Text variant="heading-md">
                        Rekomendasi Predikat Kinerja
                      </Text>
                      <Text color="subdued">
                        Rekomendasi yang Anda lakukan akan dikirim ke Pejabat
                        Penilai Kinerja.
                      </Text>
                    </div>
                    <Button as="a" className="flex-shrink-0" disabled>
                      {!isPenetapanStarted && isValid(penetapanStartDate)
                        ? `Tersedia mulai ${formatDate(penetapanStartDate)}`
                        : 'Lakukan'}
                    </Button>
                  </div>
                )}

                {/* Block "Lakukan Penetapan Rekomendasi Predikat Kinerja" */}
                {isPenetapanEnabled && isPenetapanStarted && (
                  <div className="border border-subdued rounded py-6 pl-6 pr-8 space-x-6 flex items-center">
                    <div className="w-20 h-20 flex-shrink-0 flex items-center justify-center">
                      <CircularProgressV2
                        isLoading={isLoading}
                        value={counterPenetapan.sudahDikirim}
                        maxValue={counterPenetapan.all}
                        isLocked={false}
                      />
                    </div>
                    <div className="space-y-2 w-full flex-1">
                      <Text variant="heading-md">
                        Rekomendasi Predikat Kinerja
                      </Text>

                      <Text color="subdued">
                        Rekomendasi yang Anda lakukan akan dikirim ke Pejabat
                        Penilai Kinerja.
                      </Text>
                    </div>
                    <div className="flex-1 max-w-60">
                      <Button
                        className="flex-shrink-0"
                        fullWidth
                        color="black"
                        onClick={() => {
                          if (currentStatus === 'hidden') goto()
                          else setIsOpen(true)
                        }}
                      >
                        Lakukan
                      </Button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="flex gap-6">
                <PenetapanRecommendationBlock
                  isPenetapanEnabled={isPenetapanEnabled}
                  isPenetapanStarted={isPenetapanStarted}
                  penetapanStartDate={penetapanStartDate}
                  isLoading={isLoading}
                  counter={penetapanDetail.ks}
                  onCtaClick={() => {
                    router.push(
                      `${PENGELOLAAN_KINERJA_PS_PATH}/penetapan/kepala-sekolah`
                    )
                  }}
                  title="Rekomendasi Predikat Kinerja Kepala Sekolah"
                  subtitle="Rekomendasi yang Anda lakukan akan dikirim ke Pejabat Penilai Kinerja."
                />
                <PenetapanRecommendationBlock
                  isPenetapanEnabled={isPenetapanEnabled}
                  isPenetapanStarted={isPenetapanStarted}
                  penetapanStartDate={penetapanStartDate}
                  isLoading={isLoading}
                  counter={penetapanDetail.guru}
                  onCtaClick={() => {
                    router.push(`${PENGELOLAAN_KINERJA_PS_PATH}/penetapan/guru`)
                  }}
                  title="Rekomendasi Predikat Kinerja Guru"
                  subtitle="Khusus untuk Guru yang menjabat sebagai Plt. Kepala Sekolah di satuan pendidikan yang sama. Rekomendasi yang Anda lakukan akan dikirim ke Pejabat Penilai Kinerja."
                />
              </div>
            )}
            {isKepalaDinasAndPengawasSekolah && (
              <div className="flex bg-cyan-5 rounded p-3 gap-1">
                <Icon color="informational">error</Icon>
                <Text>
                  Rangkaian Penilaian Kinerja sama persis dengan rangkaian yang
                  dilakukan oleh Tim Kerja, termasuk tampilan dan kata-kata yang
                  digunakan.
                </Text>
              </div>
            )}
          </div>
        </div>
      </section>

      <DialogPanduanPenetapanPredikatKinerja
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false)
          setStatus('hidden')
          goto()
        }}
      />
    </div>
  )
}
export default PenilaianKinerja
